/**
 * Presets de vencimento das listagens do portal, os do protótipo
 * (`lib/vencimentoFilter.ts`): "Vencido", "Vence em N dias" e "Vigente".
 *
 * "Vence em N dias" é de hoje até hoje + N; "Vigente" é qualquer data de hoje
 * em diante (inclui o que vence em breve). Documento sem data não entra em
 * preset nenhum — quem filtra por vencimento não quer ver quem nem tem data.
 *
 * Comparação por texto `AAAA-MM-DD`, no fuso local: é o formato que a coluna
 * `date` do banco devolve, e comparar como texto evita o deslocamento de fuso
 * que `new Date('2026-09-25')` (meia-noite UTC) traria.
 */
export const PRESETS_DE_VENCIMENTO = ['vencido', '30dias', '60dias', '90dias', 'vigente'] as const;
export type PresetDeVencimento = (typeof PRESETS_DE_VENCIMENTO)[number];

export const OPCOES_DE_VENCIMENTO: readonly { value: PresetDeVencimento; label: string }[] = [
  { value: 'vencido', label: 'Vencido' },
  { value: '30dias', label: 'Vence em 30 dias' },
  { value: '60dias', label: 'Vence em 60 dias' },
  { value: '90dias', label: 'Vence em 90 dias' },
  { value: 'vigente', label: 'Vigente' },
];

const DIAS_DO_PRESET: Record<Exclude<PresetDeVencimento, 'vencido' | 'vigente'>, number> = {
  '30dias': 30,
  '60dias': 60,
  '90dias': 90,
};

/** `AAAA-MM-DD` do dia local, `dias` à frente (negativo é passado). */
export function diaDaqui(dias: number, hoje: Date = new Date()): string {
  const data = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + dias);
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

export function presetValido(valor: string): PresetDeVencimento | undefined {
  return (PRESETS_DE_VENCIMENTO as readonly string[]).includes(valor)
    ? (valor as PresetDeVencimento)
    : undefined;
}

export function validadeNoPreset(
  validade: string | null,
  preset: PresetDeVencimento,
  hoje: Date = new Date(),
): boolean {
  if (!validade) {
    return false;
  }
  const dia = validade.slice(0, 10);
  const hojeTexto = diaDaqui(0, hoje);
  if (preset === 'vencido') {
    return dia < hojeTexto;
  }
  if (preset === 'vigente') {
    return dia >= hojeTexto;
  }
  return dia >= hojeTexto && dia <= diaDaqui(DIAS_DO_PRESET[preset], hoje);
}

/**
 * O alerta das colunas de documento do protótipo (`docAlert`): vencido, ou
 * vencendo em menos de 30 dias. Fora disso, nenhum alerta.
 */
export type AlertaDeValidade = 'vencido' | 'vencendo' | null;

export function alertaDeValidade(
  validade: string | null,
  hoje: Date = new Date(),
): AlertaDeValidade {
  if (!validade) {
    return null;
  }
  const dia = validade.slice(0, 10);
  if (dia < diaDaqui(0, hoje)) {
    return 'vencido';
  }
  if (dia < diaDaqui(30, hoje)) {
    return 'vencendo';
  }
  return null;
}

/** `dd/mm/aaaa` a partir de `AAAA-MM-DD`, sem passar por `Date` (e pelo fuso). */
export function formatarDia(validade: string | null): string {
  if (!validade) {
    return '—';
  }
  const [ano, mes, dia] = validade.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}
